/**
 * Aylık Muhasebe Raporu Merkezi (iter294)
 * Dönem seçer → o aya ait tüm rapor tipleri (Tahsilat, Personel, …) listelenir.
 * Satır tıklama → detay modal (önizle, PDF, muhasebeye gönder, revizyon geçmişi).
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, Btn, Select, Input, Table, Modal } from "@/components/UI";
import { Loader2, FileText, Send, RefreshCw, History, ChevronLeft, Download, AlertTriangle } from "lucide-react";

const MONTH_NAMES = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
                     "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

const STATUS_STYLES = {
  not_prepared:        "bg-slate-100 text-slate-600 border-slate-300",
  preparing:           "bg-amber-100 text-amber-800 border-amber-300",
  ready:               "bg-emerald-100 text-emerald-800 border-emerald-300",
  submitted:           "bg-indigo-100 text-indigo-800 border-indigo-300",
  revision_required:   "bg-rose-100 text-rose-800 border-rose-300",
};

function StatusBadge({ status, label }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.not_prepared;
  return <span className={`text-[11px] px-2 py-0.5 rounded border font-semibold ${cls}`}
    data-testid={`monthly-status-${status}`}>{label || status}</span>;
}

export default function MonthlyReportsPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState("");
  const [detailReport, setDetailReport] = useState(null);  // {type, year, month}

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await api.get(`/monthly-accounting/reports?year=${year}&month=${month}`);
      setReports(d.reports || []);
      setDefaultEmail(d.default_accountant_email || "");
    } catch (e) {
      alert(e.response?.data?.detail || "Rapor listesi alınamadı");
    } finally { setBusy(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Aylık Muhasebe Raporu"
        description="Muhasebeye aylık gönderilen tüm raporların merkezi listesi"
        actions={<Btn variant="ghost" onClick={load} disabled={busy} data-testid="mar-refresh">
          {busy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Yenile
        </Btn>}/>

      {/* Dönem seçici */}
      <div className="flex gap-3 items-end mb-4 bg-slate-50 border rounded p-3">
        <div>
          <label className="text-xs uppercase text-slate-500 font-semibold block mb-1">Yıl</label>
          <Select value={year} onChange={e => setYear(Number(e.target.value))} data-testid="mar-year">
            {Array.from({length: 6}, (_, i) => now.getFullYear() - i).map(y =>
              <option key={y} value={y}>{y}</option>)}
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase text-slate-500 font-semibold block mb-1">Ay</label>
          <Select value={month} onChange={e => setMonth(Number(e.target.value))} data-testid="mar-month">
            {MONTH_NAMES.map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
          </Select>
        </div>
        <div className="text-sm text-slate-600 ml-2 pb-1">
          <b>Dönem:</b> {MONTH_NAMES[month-1]} {year}
        </div>
      </div>

      <Table testid="monthly-reports-table"
        rows={reports}
        onRowClick={r => setDetailReport({ type: r.type, year, month })}
        columns={[
          { label: "Rapor Adı", render: r => (
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-indigo-600"/>
                <span className="font-semibold">{r.label}</span>
                {r.drift_detected && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-300 inline-flex items-center gap-1"
                    title="Kaynak veri gönderim sonrası değişti — revizyon gerekli">
                    <AlertTriangle size={10}/> Değişti
                  </span>
                )}
              </div>
            )},
          { label: "Dönem", render: r => `${MONTH_NAMES[r.month-1]} ${r.year}` },
          { label: "Rev.", render: r => <span className="font-mono text-xs">{r.revision}</span> },
          { label: "Durum", render: r => <StatusBadge status={r.status} label={r.status_label}/> },
          { label: "Hazırlayan", render: r => r.prepared_by ? `${r.prepared_by}` : <span className="text-slate-400">—</span> },
          { label: "Muhasebeye Gönderim", render: r => r.submitted_at ? (
              <span className="text-xs">
                <b>{r.submitted_by}</b><br/>
                <span className="text-slate-500">{(r.submitted_at||"").slice(0,16).replace("T"," ")}</span>
              </span>
            ) : <span className="text-slate-400">Gönderilmedi</span> },
          { label: "İşlem", render: r => (
              <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                <Btn size="sm" variant="secondary"
                  onClick={() => setDetailReport({ type: r.type, year, month })}
                  data-testid={`mar-open-${r.type}`}>Aç</Btn>
              </div>
            )},
        ]}
        empty={busy ? "Yükleniyor…" : "Bu dönem için henüz rapor yok"}/>

      {detailReport && (
        <ReportDetailModal
          type={detailReport.type}
          year={detailReport.year}
          month={detailReport.month}
          defaultEmail={defaultEmail}
          onClose={() => { setDetailReport(null); load(); }}/>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Detail Modal: preview, prepare, PDF download, send-to-accounting, history
// ────────────────────────────────────────────────────────────────────────────
function ReportDetailModal({ type, year, month, defaultEmail, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await api.get(`/monthly-accounting/reports/${type}/${year}/${month}`);
      setData(d);
    } catch (e) { alert(e.response?.data?.detail || "Rapor detayı alınamadı"); }
    finally { setBusy(false); }
  }, [type, year, month]);

  useEffect(() => { load(); }, [load]);

  const call = async (key, fn) => {
    setBusyKey(key);
    try { await fn(); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message || "Hata"); }
    finally { setBusyKey(null); }
  };

  const prepare = () => call("prepare", () =>
    api.post(`/monthly-accounting/reports/${type}/${year}/${month}/prepare`));
  const revision = () => call("revision", async () => {
    if (!window.confirm("Yeni revizyon (R01+) oluşturulacak. Devam?")) return;
    await api.post(`/monthly-accounting/reports/${type}/${year}/${month}/revision`);
  });

  const openPdf = () => call("pdf", async () => {
    const blob = await api.getBlob(`/monthly-accounting/reports/${type}/${year}/${month}/pdf`);
    const url = URL.createObjectURL(blob);
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(url);
    window.open(url, "_blank");
  });
  const downloadPdf = () => call("dl", async () => {
    const blob = await api.getBlob(`/monthly-accounting/reports/${type}/${year}/${month}/pdf`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data?.label || type}_${year}-${String(month).padStart(2,"0")}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  });

  if (!data && busy) {
    return <Modal open onClose={onClose} title="Yükleniyor…" size="lg">
      <div className="text-center py-8"><Loader2 className="animate-spin inline"/></div>
    </Modal>;
  }
  if (!data) return null;

  const st = data.state || {};
  const snap = data.snapshot || {};
  const isCollection = type === "collection";

  return (
    <Modal open onClose={onClose} title={`${data.label} · ${year}-${String(month).padStart(2,"0")}`} size="full">
      <div className="space-y-3">
        {/* Top action bar */}
        <div className="flex items-center justify-between border-b pb-2 flex-wrap gap-2">
          <Btn size="sm" variant="ghost" onClick={onClose} data-testid="mar-back">
            <ChevronLeft size={14}/> Rapor Listesine Dön
          </Btn>
          <div className="flex gap-2 flex-wrap">
            <Btn size="sm" onClick={prepare} disabled={!!busyKey} data-testid="mar-prepare">
              {busyKey === "prepare" ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
              {st.status ? "Yeniden Hazırla" : "Hazırla"}
            </Btn>
            <Btn size="sm" variant="secondary" onClick={openPdf} disabled={!!busyKey} data-testid="mar-pdf">
              {busyKey === "pdf" ? <Loader2 size={12} className="animate-spin"/> : <FileText size={12}/>} Önizle / PDF
            </Btn>
            <Btn size="sm" variant="secondary" onClick={downloadPdf} disabled={!!busyKey} data-testid="mar-download">
              {busyKey === "dl" ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>} İndir
            </Btn>
            <Btn size="sm" onClick={() => setSubmitOpen(true)} disabled={!!busyKey} data-testid="mar-submit">
              <Send size={12}/> Muhasebeye Gönder
            </Btn>
            {(st.submitted_at || data.drift_detected) && (
              <Btn size="sm" variant="secondary" onClick={revision} disabled={!!busyKey}
                className="!bg-amber-100 hover:!bg-amber-200 !text-amber-800" data-testid="mar-revision">
                {busyKey === "revision" ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
                Revize Rapor Oluştur
              </Btn>
            )}
          </div>
        </div>

        {/* Drift warning */}
        {data.drift_detected && (
          <div className="p-3 bg-rose-50 border border-rose-300 rounded flex items-start gap-2" data-testid="mar-drift-warning">
            <AlertTriangle size={16} className="text-rose-700 flex-shrink-0 mt-0.5"/>
            <div className="text-sm text-rose-800">
              <b>Kaynak verilerde değişiklik tespit edildi.</b> Bu rapor muhasebeye gönderildikten sonra dönem
              içindeki tahsilat/personel kayıtları değişti. Eski gönderilen PDF korunur; yeni bir revizyon
              oluşturmanız önerilir.
            </div>
          </div>
        )}

        {/* State summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <InfoBox label="Durum">
            <StatusBadge status={st.status || "not_prepared"} label={data.state ? undefined : "Hazırlanmadı"}/>
          </InfoBox>
          <InfoBox label="Revizyon"><b className="font-mono">{st.revision || "R00"}</b></InfoBox>
          <InfoBox label="Hazırlayan">{st.prepared_by || "—"}<br/>
            <span className="text-slate-500">{(st.prepared_at||"").slice(0,16).replace("T"," ")}</span></InfoBox>
          <InfoBox label="Muhasebeye Gönderildi">{st.submitted_by || "—"}<br/>
            <span className="text-slate-500">{(st.submitted_at||"").slice(0,16).replace("T"," ")}</span></InfoBox>
        </div>

        {/* Snapshot summary (per-report) */}
        {isCollection && (
          <CollectionSnapshotView snap={snap}/>
        )}
        {type === "personnel" && (
          <PersonnelSnapshotView snap={snap}/>
        )}

        {/* History */}
        <div className="border rounded">
          <div className="px-3 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <History size={12}/> Revizyon & Gönderim Geçmişi
          </div>
          <div className="max-h-48 overflow-y-auto text-xs">
            {(data.history || []).length === 0 ? (
              <div className="p-3 text-slate-400 italic">Henüz kayıt yok.</div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr><th className="text-left px-2 py-1">Tarih</th><th className="text-left px-2 py-1">İşlem</th>
                      <th className="text-left px-2 py-1">Rev</th><th className="text-left px-2 py-1">Kullanıcı</th>
                      <th className="text-left px-2 py-1">Detay</th></tr>
                </thead>
                <tbody>
                  {data.history.map(h => (
                    <tr key={h.id} className="border-t">
                      <td className="px-2 py-1">{(h.at||"").slice(0,16).replace("T"," ")}</td>
                      <td className="px-2 py-1">{h.action}</td>
                      <td className="px-2 py-1 font-mono">{h.revision}</td>
                      <td className="px-2 py-1">{h.by_name}</td>
                      <td className="px-2 py-1 text-slate-500">{h.to ? `→ ${h.to}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {submitOpen && (
        <SubmitDialog type={type} year={year} month={month}
          label={data.label} defaultEmail={defaultEmail}
          onClose={() => { setSubmitOpen(false); load(); }}/>
      )}
    </Modal>
  );
}

function InfoBox({ label, children }) {
  return (
    <div className="bg-white border rounded p-2">
      <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">{label}</div>
      <div className="text-slate-800">{children}</div>
    </div>
  );
}

function CollectionSnapshotView({ snap }) {
  const t = snap.totals || {};
  const byC = t.by_currency || {};
  return (
    <div className="border rounded">
      <div className="px-3 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-700">
        Genel Özet ({snap.totals?.receipts_count || 0} tahsilat · {snap.totals?.companies_count || 0} firma)
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        {Object.keys(byC).length === 0 ? (
          <div className="col-span-4 text-slate-400 italic">Bu dönemde tahsilat yok.</div>
        ) : Object.entries(byC).map(([c, v]) => (
          <div key={c} className="bg-emerald-50 border border-emerald-200 rounded p-2">
            <div className="text-[10px] uppercase text-emerald-700 font-bold">{c}</div>
            <div className="font-mono font-semibold text-emerald-900">{v.toLocaleString("tr-TR", {minimumFractionDigits:2})}</div>
          </div>
        ))}
      </div>
      {(snap.by_company || []).length > 0 && (
        <>
          <div className="px-3 py-2 border-t border-b bg-slate-50 text-xs font-semibold text-slate-700">Firma Bazlı</div>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr><th className="text-left px-2 py-1">Firma</th><th className="text-left px-2 py-1">Adet</th>
                    <th className="text-left px-2 py-1">Toplam (PB bazlı)</th></tr>
              </thead>
              <tbody>
                {snap.by_company.map((c, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1">{c.customer_name}</td>
                    <td className="px-2 py-1">{c.count}</td>
                    <td className="px-2 py-1 font-mono">
                      {Object.entries(c.by_currency || {}).map(([k, v]) => `${k} ${v.toLocaleString("tr-TR", {minimumFractionDigits:2})}`).join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {(snap.rows || []).length > 0 && (
        <>
          <div className="px-3 py-2 border-t border-b bg-slate-50 text-xs font-semibold text-slate-700">
            Detaylı Tahsilat Listesi ({snap.rows.length})
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1">Tarih</th>
                  <th className="text-left px-2 py-1">Makbuz</th>
                  <th className="text-left px-2 py-1">Firma</th>
                  <th className="text-left px-2 py-1">Yöntem</th>
                  <th className="text-left px-2 py-1">Tutar</th>
                  <th className="text-left px-2 py-1">Çek</th>
                </tr>
              </thead>
              <tbody>
                {snap.rows.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1">{r.date}</td>
                    <td className="px-2 py-1 font-mono">{r.receipt_no}</td>
                    <td className="px-2 py-1">{r.customer_name}</td>
                    <td className="px-2 py-1">{r.method_label || r.method}</td>
                    <td className="px-2 py-1 font-mono text-right">{r.amount.toLocaleString("tr-TR", {minimumFractionDigits:2})} {r.currency}</td>
                    <td className="px-2 py-1 text-[10px] text-slate-500">
                      {(r.method === "CHECK" || r.method === "CHEQUE") ? (
                        r.check_number
                          ? `#${r.check_number} · ${r.check_bank || ""} · ${r.check_due_date || ""}`
                          : "eksik — kontrol"
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function PersonnelSnapshotView({ snap }) {
  const rows = snap.rows || [];
  const t = snap.totals || {};
  const boundaries = rows.filter(r => r.spans_month_boundary);
  return (
    <div className="border rounded">
      <div className="px-3 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-700">
        Genel Özet ({t.movements_count || 0} hareket · {t.employees_count || 0} farklı personel)
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        {Object.keys(t.by_kind || {}).length === 0 ? (
          <div className="col-span-4 text-slate-400 italic">Seçilen dönemde kayıt bulunamadı.</div>
        ) : Object.entries(t.by_kind).map(([k, v]) => (
          <div key={k} className="bg-indigo-50 border border-indigo-200 rounded p-2">
            <div className="text-[10px] uppercase text-indigo-700 font-bold">{k}</div>
            <div className="font-mono font-semibold text-indigo-900">{v}</div>
          </div>
        ))}
      </div>
      {(t.orphan_movements || []).length > 0 && (
        <div className="px-3 py-2 border-t bg-rose-50 text-[11px] text-rose-800">
          <b>UYARI:</b> {t.orphan_movements.length} hareket kaydında personel ID eksik — veri ilişki hatası.
        </div>
      )}
      {boundaries.length > 0 && (
        <div className="px-3 py-2 border-t bg-amber-50 text-[11px] text-amber-800">
          <b>Ay Sınırını Aşan {boundaries.length} İzin:</b> Gerçek aralık PDF'de görünür; ay içi kesit bilgi amaçlıdır.
        </div>
      )}
      {rows.length > 0 && (
        <>
          <div className="px-3 py-2 border-t border-b bg-slate-50 text-xs font-semibold text-slate-700">
            Detaylı Hareket Listesi ({rows.length})
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1">Personel</th>
                  <th className="text-left px-2 py-1">Tür</th>
                  <th className="text-left px-2 py-1">Başlangıç</th>
                  <th className="text-left px-2 py-1">Bitiş</th>
                  <th className="text-left px-2 py-1">Süre</th>
                  <th className="text-left px-2 py-1">Açıklama</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className={`border-t ${r.spans_month_boundary ? "bg-amber-50/60" : ""}`}>
                    <td className="px-2 py-1">{r.employee_name}</td>
                    <td className="px-2 py-1">{r.kind_label}</td>
                    <td className="px-2 py-1 font-mono">{r.start_date}</td>
                    <td className="px-2 py-1 font-mono">{r.end_date}</td>
                    <td className="px-2 py-1 font-mono">{r.duration ? `${r.duration} ${r.duration_unit}` : "—"}</td>
                    <td className="px-2 py-1 text-slate-600">
                      {r.note || ""}
                      {r.spans_month_boundary && (
                        <span className="ml-1 text-[10px] text-amber-700">
                          (ay kesiti: {r.overlap_start} → {r.overlap_end})
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SubmitDialog({ type, year, month, label, defaultEmail, onClose }) {
  const [to, setTo] = useState(defaultEmail || "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(`Meta Pharma — ${label} ${year}-${String(month).padStart(2,"0")}`);
  const [body, setBody] = useState(`Ekte ${year}-${String(month).padStart(2,"0")} dönemi ${label} bulunmaktadır.`);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!to.trim()) return alert("Alıcı e-posta zorunlu");
    if (!window.confirm(`${to} adresine ${label} PDF olarak gönderilecek. Devam?`)) return;
    setBusy(true);
    try {
      const ccList = cc.split(",").map(s => s.trim()).filter(Boolean);
      const res = await api.post(`/monthly-accounting/reports/${type}/${year}/${month}/submit`, {
        to_addr: to, subject, body, cc: ccList,
      });
      if (res.sent) { alert("Muhasebeye gönderildi ✓"); onClose(); }
      else alert("Gönderim başarısız: " + (res.result?.error || JSON.stringify(res.result || {})));
    } catch (e) { alert(e.response?.data?.detail || "Gönderim hatası"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Muhasebeye Gönder" size="lg">
      <div className="space-y-3">
        <Input label="Alıcı E-posta *" value={to} onChange={e => setTo(e.target.value)}
          placeholder="muhasebe@example.com" data-testid="mar-submit-to"/>
        <Input label="CC (virgülle ayrılmış)" value={cc} onChange={e => setCc(e.target.value)}
          placeholder="ekstra@example.com" data-testid="mar-submit-cc"/>
        <Input label="Konu" value={subject} onChange={e => setSubject(e.target.value)}
          data-testid="mar-submit-subject"/>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500 block mb-1">Mesaj</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={4}
            className="w-full px-2.5 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="mar-submit-body"/>
        </div>
        <div className="text-[11px] text-slate-500 italic">
          Rapor PDF olarak otomatik ek olarak eklenecek. Alıcı gönderim öncesi onayınızla e-posta atacaktır.
        </div>
        <div className="flex justify-end gap-2">
          <Btn size="sm" variant="secondary" onClick={onClose} disabled={busy} data-testid="mar-submit-cancel">Vazgeç</Btn>
          <Btn size="sm" onClick={send} disabled={busy} data-testid="mar-submit-send">
            {busy ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>} Gönder
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
